import React from "react";
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Text,
  Platform,
  Pressable,
  Image,
  PanResponder,
  Dimensions,
  ScrollView,
} from "react-native";
import { ResizeMode, Video } from "expo-av";

import { RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";

import { connect } from "react-redux";
import { addThotToChain, setChainEntry, setReloadHomeList } from "../../redux/actions";

import { apiGetUserVote, apiAddUserVote, apiUpdateUserVote, apiDeleteUserVote } from "../../api/vote.api";
import { apiLoadThotChain, apiGetThot, apiGetThotUri, apiGetThotThumbnailUri } from "../../api/thots.api";
import { apiGetProfileUri } from "../../api/users.api";

import { Chain } from "../../models/Chain";
import { StackNavigatorParam } from "../../models/StackNavigatorParam";
import { ThotType } from "../../models/Thot.type";
import { Vote } from "../../models/Vote";

import screenStyle from "../../styles/screen.style";
import colorStyle from "../../styles/color.style";
import { getRouteEntryId } from "../../libs/thotRoute";
import convertDate from "../../libs/convertDate";

const NO_PROFILE = require("../../../assets/no_profile.png");

type Props = {
  navigation: StackNavigationProp<StackNavigatorParam, "Thot">;
  route: RouteProp<StackNavigatorParam, "Thot">;
  chain: Chain;
  addThotToChain: Function;
  setChainEntry: Function;
  setReloadHomeList: Function;
};

type State = {
  thot: ThotType | null;
  vote: Vote;
  chainLoaded: boolean;
  autoplay: boolean;
  isVideoPlaying: boolean;
  storyMode: boolean;
  replyLoopStartId: string | null;
  selectedPlayLeafId: string | null;
  previewNodeId: string | null;
  previewBranchRootId: string | null;
  headerNeedsExpand: boolean;
  headerMeasuredTitle: string | null;
  headerTitleWidth: number;
  headerBubbleHeight: number;
  headerTitleTextHeight: number;
  headerContentHeight: number;
  headerCollapsedByUser: boolean;
  headerExpandedByUser: boolean;
  headerBackstoryOpen: boolean;
  treeCenterId: string | null;
  zoomLevel: 0 | 1;
  treeWidth: number;
  treeHeight: number;
  treePanX: number;
  treePanY: number;
  blockedNudgeX: number;
  blockedNudgeY: number;
  isDraggingTree: boolean;
  videoReadyForDisplay: boolean;
  videoPositionMillis: number;
  videoDurationMillis: number;
};

class ThotScreen extends React.Component<Props, State> {
  _video: Video;
  _resumeVideo: boolean = false;
  _panResponder: any;
  _backstoryPanResponder: any;
  _pinchStartDistance: number | null = null;
  _pinchStartLevel: 0 | 1 = 0;
  _panStartX: number = 0;
  _panStartY: number = 0;
  _blockedNudgeTimer: ReturnType<typeof setTimeout> | null = null;
  _nodePressMeta: Record<string, { startedAt: number; startX: number; startY: number; moved: boolean }> = {};
  _suppressedNodePressUntil: Record<string, number> = {};
  _suppressPressUntil: number = 0;
  _headerBackstoryScroll: ScrollView | null = null;
  _handleWindowResize = () => {
    if (Platform.OS !== "web") return;
    this.setState({ headerTitleWidth: 0, headerMeasuredTitle: null, headerNeedsExpand: false }, () => {
      this._updateHeader();
    });
  };

  constructor(props) {
    super(props);

    const thot = this.props.route.params?.thot || null;

    this.state = {
      thot,
      vote: null,
      chainLoaded: false,
      autoplay: true,
      isVideoPlaying: true,
      storyMode: true,
      replyLoopStartId: null,
      selectedPlayLeafId: this.props.route.params?.playLeafId || null,
      previewNodeId: null,
      previewBranchRootId: null,
      headerNeedsExpand: false,
      headerMeasuredTitle: null,
      headerTitleWidth: 0,
      headerBubbleHeight: 22,
      headerTitleTextHeight: 22,
      headerContentHeight: 22,
      headerCollapsedByUser: false,
      headerExpandedByUser: false,
      headerBackstoryOpen: false,
      treeCenterId: null,
      zoomLevel: 0,
      treeWidth: 0,
      treeHeight: 0,
      treePanX: 0,
      treePanY: 0,
      blockedNudgeX: 0,
      blockedNudgeY: 0,
      isDraggingTree: false,
      videoReadyForDisplay: false,
      videoPositionMillis: 0,
      videoDurationMillis: 0,
    };

    this._panResponder = PanResponder.create({
      onStartShouldSetPanResponder: () => false,
      onMoveShouldSetPanResponder: (_, g) => Math.abs(g.dx) > 5 || Math.abs(g.dy) > 5,
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: (evt) => {
        if (evt.nativeEvent.touches?.length === 2) {
          this._pinchStartDistance = this._touchDistance(evt.nativeEvent.touches);
          this._pinchStartLevel = this.state.zoomLevel;
          return;
        }

        if (this.state.zoomLevel > 0) {
          this._panStartX = this.state.treePanX;
          this._panStartY = this.state.treePanY;
          this.setState({ isDraggingTree: true });
        }
      },
      onPanResponderMove: (evt, gestureState) => {
        if (evt.nativeEvent.touches?.length === 2) {
          if (this._pinchStartDistance == null) {
            this._pinchStartDistance = this._touchDistance(evt.nativeEvent.touches);
            this._pinchStartLevel = this.state.zoomLevel;
            return;
          }

          const current = this._touchDistance(evt.nativeEvent.touches);
          const scale = current / this._pinchStartDistance;
          if (scale > 1.16) this._setZoomLevel(Math.min(1, this._pinchStartLevel + 1) as 0 | 1);
          else if (scale < 0.86) this._setZoomLevel(Math.max(0, this._pinchStartLevel - 1) as 0 | 1);
          return;
        }

        if (this.state.zoomLevel > 0) {
          this.setState({
            treePanX: this._panStartX + gestureState.dx,
            treePanY: this._panStartY + gestureState.dy,
            isDraggingTree: true,
          });
        }
      },
      onPanResponderRelease: (evt, gestureState) => {
        this._pinchStartDistance = null;

        if (evt.nativeEvent.changedTouches?.length > 1) return;

        const { dx, dy } = gestureState;
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);

        if (this.state.zoomLevel > 0) {
          if (absX > 3 || absY > 3) this._suppressPressUntil = Date.now() + 180;
          this.setState({ isDraggingTree: false }, () => {
            if (absX > 3 || absY > 3) this._snapTreeToNearestThot();
            else this.setState({ previewNodeId: null, previewBranchRootId: null });
          });
          return;
        }

        if (absX < 35 && absY < 35) return;

        if (absX > absY) {
          if (dx < 0) this._goSwipeChild();
          else this._goSwipeParent();
        } else {
          if (dy < 0) this._goSibling(1);
          else this._goSibling(-1);
        }
      },
      onPanResponderTerminate: () => {
        if (this.state.zoomLevel > 0) this.setState({ isDraggingTree: false, previewNodeId: null, previewBranchRootId: null });
        this._pinchStartDistance = null;
      },
    });

    this._backstoryPanResponder = PanResponder.create({
      onStartShouldSetPanResponder: () => false,
      onMoveShouldSetPanResponder: (_, g) => {
        if (!this.state.headerBackstoryOpen) return false;
        const absX = Math.abs(g.dx);
        const absY = Math.abs(g.dy);
        return absX > 70 && absX > absY * 1.35;
      },
      onPanResponderTerminationRequest: () => false,
      onPanResponderRelease: (_, gestureState) => {
        const { dx, dy } = gestureState;
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);

        if (absX < 35 && absY < 35) return;

        if (absX > absY) {
          if (dx < 0) this._goSwipeChild();
          else this._goSwipeParent();
        }
      },
    });
  }

  componentDidMount() {
    this._updateHeader();
    this._initFromRoute();
    if (Platform.OS === "web" && typeof window !== "undefined") window.addEventListener("resize", this._handleWindowResize);
  }

  componentWillUnmount() {
    if (this._blockedNudgeTimer) clearTimeout(this._blockedNudgeTimer);
    if (Platform.OS === "web" && typeof window !== "undefined") window.removeEventListener("resize", this._handleWindowResize);
  }

  componentDidUpdate(prevProps, prevState) {
    const routeThotId = this.props.route.params?.thotId || this.props.route.params?.thot?._id;
    const prevRouteThotId = prevProps.route.params?.thotId || prevProps.route.params?.thot?._id;

    if (routeThotId && routeThotId !== prevRouteThotId && routeThotId !== this.state.thot?._id) {
      this._initFromRoute();
      return;
    }

    const currentId = this.state.thot?._id;
    const prevId = prevState.thot?._id;
    const currentEntryId = this.props.chain.entry?._id;
    const prevEntryId = prevProps.chain.entry?._id;

    if (currentId && (currentId !== prevId || currentEntryId !== prevEntryId)) {
      this._syncUrlParams();
    }

    const currentHeaderTitle = this._getHeaderDisplayTitle();
    const prevPreviewNode = prevState.previewNodeId;
    const currentPreviewNode = this.state.previewNodeId;
    const prevHeaderThot =
      (prevPreviewNode && this.props.chain.thots?.[prevPreviewNode]) || prevState.thot;
    const prevHeaderTitle =
      (!prevPreviewNode && prevState.headerBackstoryOpen && prevHeaderThot
        ? this._buildLineToRootFromThot(prevHeaderThot)
            .map((id) => prevProps.chain.thots?.[id]?.title?.trim())
            .filter(Boolean)
            .join("\n")
        : prevHeaderThot?.title) || "Gedanke";
    const currentIsPreviewTitle = !!currentPreviewNode && currentPreviewNode !== currentId;

    const headerTitleChanged =
      currentHeaderTitle !== prevHeaderTitle ||
      currentPreviewNode !== prevPreviewNode ||
      this.state.headerTitleWidth !== prevState.headerTitleWidth ||
      this.state.headerBackstoryOpen !== prevState.headerBackstoryOpen ||
      this.state.headerExpandedByUser !== prevState.headerExpandedByUser ||
      this.state.headerCollapsedByUser !== prevState.headerCollapsedByUser;

    if (
      currentHeaderTitle !== prevHeaderTitle ||
      currentPreviewNode !== prevPreviewNode ||
      this.state.headerTitleWidth !== prevState.headerTitleWidth ||
      this.state.headerBackstoryOpen !== prevState.headerBackstoryOpen ||
      this.state.headerExpandedByUser !== prevState.headerExpandedByUser ||
      this.state.headerCollapsedByUser !== prevState.headerCollapsedByUser
    ) {
      this._measureHeaderExpansion(currentHeaderTitle, currentIsPreviewTitle, this._updateHeader);
    }

    if (
      currentId !== prevId ||
      (!headerTitleChanged &&
        (
          this.state.headerNeedsExpand !== prevState.headerNeedsExpand ||
          this.state.headerBackstoryOpen !== prevState.headerBackstoryOpen ||
          this.state.headerExpandedByUser !== prevState.headerExpandedByUser ||
          this.state.headerCollapsedByUser !== prevState.headerCollapsedByUser
        ))
    ) {
      this._updateHeader();
    }
  }

  _touchDistance = (touches) => {
    if (!touches || touches.length < 2) return 0;
    const [a, b] = touches;
    const dx = a.pageX - b.pageX;
    const dy = a.pageY - b.pageY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  _getZoomScale = (zoomLevel = this.state.zoomLevel, layout = this._getTreeLayout(), thot = this.state.thot) => {
    if (zoomLevel === 0 && layout && thot && this.state.treeWidth > 0) {
      const leftPadding = 40;
      const rightPadding = 40;
      return Math.max((this.state.treeWidth - leftPadding - rightPadding) / Math.max(layout.nodeW, 1), 0.01);
    }

    const zoomScales = [2.2, 0.45];
    return zoomScales[zoomLevel] || zoomScales[0];
  };

  _setZoomLevel = (zoomLevel: 0 | 1) =>
    this.setState({ zoomLevel, treePanX: 0, treePanY: 0, blockedNudgeX: 0, blockedNudgeY: 0, treeCenterId: null, previewNodeId: null, previewBranchRootId: null, isDraggingTree: false });

  _getHeaderThot = () => {
    const { thot, previewNodeId } = this.state;
    const { thots } = this.props.chain;
    if (previewNodeId && thots?.[previewNodeId]) return thots[previewNodeId];
    return thot;
  };

  _isPreviewHeader = () => !!this.state.previewNodeId && this.state.previewNodeId !== this.state.thot?._id;

  _getHeaderDisplayTitle = () => {
    const headerThot = this._getHeaderThot();
    if (!headerThot) return "Gedanke";
    if (!this._isPreviewHeader() && this.state.headerBackstoryOpen) {
      return this._buildLineToRootFromThot(headerThot)
        .map((id) => {
          const item = this.props.chain.thots?.[id];
          if (!item) return null;
          const user = item.user && typeof item.user !== "string" ? item.user : null;
          const nick = user?.name ? `@${user.name}` : "";
          const date = item.created_at ? convertDate(item.created_at) : "";
          const meta = [nick, date].filter(Boolean).join(" ");
          return [item.title?.trim(), meta].filter(Boolean).join("\n");
        })
        .filter(Boolean)
        .join("\n\n");
    }
    return headerThot.title?.trim() || "Gedanke";
  };

  _getHeaderBackstoryItems = () => {
    const headerThot = this._getHeaderThot();
    if (!headerThot) return [];
    return this._buildLineToRootFromThot(headerThot)
      .map((id) => {
        const item = this.props.chain.thots?.[id];
        if (!item) return null;
        const user = item.user && typeof item.user !== "string" ? item.user : null;
        const nick = user?.name ? `@${user.name}` : "";
        const date = item.created_at ? convertDate(item.created_at) : "";
        return {
          id,
          title: item.title?.trim() || "Gedanke",
          meta: [nick, date].filter(Boolean).join(" "),
        };
      })
      .filter(Boolean);
  };

  _getHeaderMeta = () => {
    const headerThot = this._getHeaderThot();
    const user = headerThot?.user && typeof headerThot.user !== "string" ? headerThot.user : null;
    const nick = user?.name ? `@${user.name}` : "";
    const date = headerThot?.created_at ? convertDate(headerThot.created_at) : "";
    return [nick, date].filter(Boolean).join(" ");
  };

  _getHeaderShouldExpand = () => {
    const title = this._getHeaderDisplayTitle();
    const isPreviewTitle = this._isPreviewHeader();
    if (isPreviewTitle) {
      if (this.state.headerMeasuredTitle === title) return this.state.headerNeedsExpand;
      return true;
    }
    if (this.state.headerBackstoryOpen) return true;
    if (this.state.headerCollapsedByUser) return false;
    return this.state.headerExpandedByUser || (this.state.headerMeasuredTitle === title && this.state.headerNeedsExpand);
  };

  _toggleHeaderExpanded = () => {
    if (this._isPreviewHeader()) return;
    if (this._getHeaderShouldExpand()) return;
    this.setState({ headerCollapsedByUser: false, headerExpandedByUser: true, headerBackstoryOpen: false }, () => {
      this._measureHeaderExpansion(this._getHeaderDisplayTitle(), false, this._updateHeader);
    });
  };

  _collapseHeader = () => {
    if (this._isPreviewHeader()) return;
    if (this.state.headerBackstoryOpen) {
      this.setState({ headerCollapsedByUser: false, headerExpandedByUser: true, headerBackstoryOpen: false }, () => {
        this._measureHeaderExpansion(this._getHeaderDisplayTitle(), false, this._updateHeader);
      });
      return;
    }
    this.setState({ headerCollapsedByUser: true, headerExpandedByUser: false, headerBackstoryOpen: false }, () => {
      this._measureHeaderExpansion(this._getHeaderDisplayTitle(), false, this._updateHeader);
    });
  };

  _openHeaderBackstory = () => {
    if (this._isPreviewHeader() || this.state.headerBackstoryOpen) return;
    if (!this._getHeaderShouldExpand()) {
      if (!this.state.headerNeedsExpand) {
        this.setState({ headerCollapsedByUser: true, headerExpandedByUser: false, headerBackstoryOpen: true }, () => {
          this._measureHeaderExpansion(this._getHeaderDisplayTitle(), false, this._updateHeader);
        });
      } else {
        this.setState({ headerCollapsedByUser: false, headerExpandedByUser: true, headerBackstoryOpen: false }, () => {
          this._measureHeaderExpansion(this._getHeaderDisplayTitle(), false, this._updateHeader);
        });
      }
      return;
    }
    this.setState({ headerCollapsedByUser: false, headerExpandedByUser: true, headerBackstoryOpen: true }, () => {
      this._measureHeaderExpansion(this._getHeaderDisplayTitle(), false, this._updateHeader);
    });
  };

  _scrollHeaderBackstoryToEnd = () => {
    requestAnimationFrame(() => {
      this._headerBackstoryScroll?.scrollToEnd?.({ animated: false });
    });
  };

  _measureHeaderExpansion = (title: string, isPreviewTitle: boolean, done?: () => void) => {
    if (Platform.OS !== "web" || typeof document === "undefined" || this.state.headerTitleWidth <= 0) {
      done?.();
      return;
    }

    const probe = document.createElement("div");
    probe.textContent = title;
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.pointerEvents = "none";
    probe.style.left = "-99999px";
    probe.style.top = "0";
    probe.style.width = `${Math.max(this.state.headerTitleWidth - 8, 0)}px`;
    probe.style.whiteSpace = "normal";
    probe.style.wordBreak = "break-word";
    probe.style.fontSize = "17px";
    probe.style.fontWeight = "600";
    probe.style.lineHeight = "22px";
    probe.style.textAlign = "center";
    probe.style.paddingLeft = "54px";
    probe.style.paddingRight = "54px";
    probe.style.boxSizing = "border-box";
    document.body.appendChild(probe);
    const textHeight = Math.max(22, Math.ceil(probe.scrollHeight));
    const needsExpand = textHeight > 22;
    const multilineExtraHeight = isPreviewTitle && needsExpand ? 20 : 0;
    const shouldShowPersistentTags = !isPreviewTitle;
    const shouldShowHideTag = !isPreviewTitle && (this.state.headerBackstoryOpen || this.state.headerExpandedByUser || (!this.state.headerCollapsedByUser && needsExpand));
    const tagsHeight = shouldShowPersistentTags ? 34 : 0;
    const viewportHeight = Dimensions.get("window").height || this.state.treeHeight || 0;
    const expandedBottomPadding = !this.state.headerBackstoryOpen && !this.state.headerCollapsedByUser ? 40 : 0;
    const expandedHeightReduction = !this.state.headerBackstoryOpen && !this.state.headerCollapsedByUser ? 40 : 0;
    const collapsedContentHeight = 22 + tagsHeight;
    const contentHeight = isPreviewTitle
      ? textHeight + multilineExtraHeight
      : this.state.headerBackstoryOpen
      ? textHeight + tagsHeight
      : (this.state.headerCollapsedByUser ? collapsedContentHeight : Math.max(22 + tagsHeight, textHeight + multilineExtraHeight + tagsHeight + expandedBottomPadding - expandedHeightReduction));
    const bubbleHeight = isPreviewTitle
      ? Math.max(22, textHeight + multilineExtraHeight + 17)
      : this.state.headerBackstoryOpen
      ? Math.max(viewportHeight, 22)
      : Math.max(22, (this.state.headerCollapsedByUser ? collapsedContentHeight : Math.max(22 + tagsHeight, textHeight + multilineExtraHeight + tagsHeight + expandedBottomPadding - expandedHeightReduction)) + 17);
    document.body.removeChild(probe);

    if (
      this.state.headerMeasuredTitle === title &&
      this.state.headerNeedsExpand === needsExpand &&
      this.state.headerBubbleHeight === bubbleHeight &&
      this.state.headerTitleTextHeight === textHeight &&
      this.state.headerContentHeight === contentHeight
    ) {
      done?.();
      return;
    }
    this.setState({
      headerMeasuredTitle: title,
      headerNeedsExpand: needsExpand,
      headerBubbleHeight: bubbleHeight,
      headerTitleTextHeight: textHeight,
      headerContentHeight: contentHeight,
    }, done);
  };

  _onHeaderTitleWrapLayout = (evt) => {
    const width = Math.round(evt?.nativeEvent?.layout?.width || 0);
    if (!width || width === this.state.headerTitleWidth) return;
    this.setState({ headerTitleWidth: width });
  };

  _updateHeader = () => {
    const headerThot = this._getHeaderThot();
    const isPreviewTitle = this._isPreviewHeader();
    const title = this._getHeaderDisplayTitle();
    const meta = this._getHeaderMeta();
    const shouldExpandHeader = this._getHeaderShouldExpand();
    const showPersistentTags = !!meta || isPreviewTitle || !isPreviewTitle;
    const showHideTag = shouldExpandHeader && !isPreviewTitle && (this.state.headerBackstoryOpen || this.state.headerNeedsExpand);
    const collapsedBubbleHeight = Math.max(22, 22 + 17 + (showPersistentTags ? 34 : 0));
    const bubbleHeight = shouldExpandHeader ? this.state.headerBubbleHeight : collapsedBubbleHeight;
    const bubbleContentHeight = shouldExpandHeader
      ? this.state.headerContentHeight
      : Math.max(22, 22 + (showPersistentTags ? 34 : 0));
    const backstoryGradientHeight = Math.max(this.state.treeHeight || Dimensions.get("window").height || 0, 56);

    this.props.navigation.setOptions({
      title,
      headerTransparent: true,
      headerBackground: () => (
        <View
          pointerEvents="none"
          style={[
            styles.headerGradientBackground,
            this.state.headerBackstoryOpen && { height: backstoryGradientHeight },
          ]}
        >
          <View
            style={[
              styles.headerGradientLayer,
              styles.headerGradientLayerDefault,
              this.state.headerBackstoryOpen && styles.headerGradientLayerHidden,
            ]}
          />
          <View
            style={[
              styles.headerGradientLayer,
              styles.headerGradientLayerBackstory,
              this.state.headerBackstoryOpen ? styles.headerGradientLayerVisible : styles.headerGradientLayerHidden,
            ]}
          />
        </View>
      ),
      headerTitle: () => (
        <View style={styles.headerTitleWrap} onLayout={this._onHeaderTitleWrapLayout}>
          <View
            style={[
              styles.headerTitleBubble,
              {
                height: bubbleHeight,
                top: this.state.headerBackstoryOpen ? 0 : 17,
                ...(Platform.OS === "web"
                  ? (this.state.headerBackstoryOpen
                      ? ({
                          backgroundImage: "none",
                        } as any)
                      : ({
                          backgroundImage: `linear-gradient(to bottom, ${colorStyle.appBg} 0px, ${colorStyle.appBg} ${Math.max(22, Math.min(bubbleContentHeight, bubbleHeight))}px, rgba(237,235,234,0) 100%)`,
                        } as any))
                  : { backgroundColor: colorStyle.appBg }),
                paddingBottom: !this.state.headerBackstoryOpen && shouldExpandHeader ? 40 : 0,
              },
            ]}
          >
            {this.state.headerBackstoryOpen ? (
              <ScrollView
                ref={(ref) => {
                  this._headerBackstoryScroll = ref;
                }}
                style={styles.headerBackstoryScroll}
                contentContainerStyle={styles.headerBackstoryScrollContent}
                showsVerticalScrollIndicator={false}
                onContentSizeChange={this._scrollHeaderBackstoryToEnd}
                {...this._backstoryPanResponder.panHandlers}
              >
                <View style={styles.headerBackstoryList}>
                  {this._getHeaderBackstoryItems().map((item: any, index: number, list: any[]) => (
                    <View key={item.id} style={styles.headerBackstoryItem}>
                      <Text style={styles.headerTitle}>{item.title}</Text>
                      {item.meta && index < list.length - 1 ? (
                        <View style={[styles.headerTag, styles.headerMetaTag, styles.headerBackstoryMetaTag]}>
                          <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerTagText}>{item.meta}</Text>
                        </View>
                      ) : null}
                    </View>
                  ))}
                </View>
                {showPersistentTags ? (
                  <View style={styles.headerTagRow}>
                    {meta ? (
                      <View style={[styles.headerTag, styles.headerMetaTag]}>
                        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerTagText}>{meta}</Text>
                      </View>
                    ) : null}
                    {showHideTag ? (
                      <Pressable style={[styles.headerTag, styles.headerActionTag]} onPress={this._collapseHeader}>
                        <Text style={styles.headerTagText}>(っ ̫-)</Text>
                      </Pressable>
                    ) : null}
                    {!this.state.headerBackstoryOpen ? (
                      <Pressable
                        style={[styles.headerTag, styles.headerActionTag]}
                        onPress={this._openHeaderBackstory}
                      >
                        <Text style={styles.headerTagText}>(╭ರ_•́)</Text>
                      </Pressable>
                    ) : null}
                  </View>
                ) : null}
              </ScrollView>
            ) : (
              <Pressable onPress={this._toggleHeaderExpanded} style={styles.headerTitlePress}>
                <Text numberOfLines={shouldExpandHeader ? 0 : 1} ellipsizeMode="tail" style={styles.headerTitle}>
                  {title}
                </Text>
              </Pressable>
            )}
            {!this.state.headerBackstoryOpen && showPersistentTags ? (
              <View style={styles.headerTagRow}>
                {meta ? (
                  <View style={[styles.headerTag, styles.headerMetaTag]}>
                    <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerTagText}>{meta}</Text>
                  </View>
                ) : null}
                {isPreviewTitle ? (
                  <View style={[styles.headerTag, styles.headerActionTag, styles.headerActionTagDisabled]}>
                    <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerTagText}>preview</Text>
                  </View>
                ) : null}
                {showHideTag ? (
                  <Pressable style={[styles.headerTag, styles.headerActionTag]} onPress={this._collapseHeader}>
                    <Text style={styles.headerTagText}>(っ ̫-)</Text>
                  </Pressable>
                ) : null}
                {!this.state.headerBackstoryOpen && !isPreviewTitle ? (
                  <Pressable
                    style={[styles.headerTag, styles.headerActionTag]}
                    onPress={this._openHeaderBackstory}
                  >
                    <Text style={styles.headerTagText}>(╭ರ_•́)</Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}
          </View>
        </View>
      ),
      headerTitleContainerStyle: styles.headerTitleContainer,
      headerStyle: styles.header,
      headerLeftContainerStyle: styles.headerNavItem,
      headerRightContainerStyle: styles.headerNavItem,
      headerBackTitleVisible: false,
      headerLeft: () => null,
      headerRight: () => null,
    });
  };

  _initFromRoute = async () => {
    let thot = this.props.route.params?.thot;

    if (!thot && this.props.route.params?.thotId) {
      try {
        thot = await apiGetThot(this.props.route.params.thotId);
      } catch (_) {
        this._closeThot();
        return;
      }
    }

    if (!thot) {
      this._closeThot();
      return;
    }

    this.setState(
      {
        thot,
        selectedPlayLeafId: this.props.route.params?.playLeafId || null,
        replyLoopStartId: null,
        videoReadyForDisplay: false,
        isVideoPlaying: true,
        treeCenterId: thot._id,
        previewNodeId: null,
        previewBranchRootId: null,
        headerCollapsedByUser: false,
        headerExpandedByUser: false,
        headerBackstoryOpen: false,
        treePanX: 0,
        treePanY: 0,
        blockedNudgeX: 0,
        blockedNudgeY: 0,
        isDraggingTree: false,
      },
      () => {
        this._getUserVoteForThot();
        this._checkChain();
        this._syncUrlParams();
      }
    );
  };

  _syncUrlParams = () => {
    if (Platform.OS !== "web") return;

    const { thot, selectedPlayLeafId } = this.state;
    if (!thot) return;

    const params = this.props.route.params || ({} as any);
    const nextEntryId = getRouteEntryId(params, this.props.chain.entry?._id || thot._id);
    const nextPlayLeafId = selectedPlayLeafId || undefined;

    if (params.thotId !== thot._id || params.entryId !== nextEntryId || params.playLeafId !== nextPlayLeafId) {
      this.props.navigation.setParams({ thotId: thot._id, entryId: nextEntryId, playLeafId: nextPlayLeafId });
    }
  };

  _getUserVoteForThot = () => {
    const { thot } = this.state;
    if (!thot) return;

    apiGetUserVote(thot._id)
      .then((res: Vote) => this.setState({ vote: res }))
      .catch((_) => {});
  };

  _applyRouteEntry = (thot: ThotType) => {
    const routeEntryId = getRouteEntryId(this.props.route.params, thot._id);
    const routeEntry = routeEntryId ? this.props.chain.thots[routeEntryId] : null;

    if (routeEntry) this.props.setChainEntry(routeEntry);
    else if (!this.props.route.params?.unchanged) this.props.setChainEntry(thot);
  };

  _findDefaultPlayLeaf = (start?: ThotType): ThotType | undefined => {
    const { thots } = this.props.chain;
    if (!thots || !start) return undefined;

    const visited = new Set<string>();
    let cursor: ThotType | undefined = start;

    while (cursor && !visited.has(cursor._id)) {
      visited.add(cursor._id);
      const next = this._getFirstChild(cursor);
      if (!next || next._id === cursor._id) return cursor;
      cursor = next;
    }

    return cursor;
  };

  _resolvePlayLeaf = (start?: ThotType, preferredPlayLeafId?: string | null): ThotType | undefined => {
    const { thots } = this.props.chain;
    if (!thots || !start) return undefined;

    const preferredLeaf = preferredPlayLeafId ? thots[preferredPlayLeafId] : undefined;
    if (preferredLeaf) {
      const preferredLine = this._buildLineToRootFromThot(preferredLeaf);
      if (preferredLine.includes(start._id)) return preferredLeaf;
    }

    return this._findDefaultPlayLeaf(start);
  };

  _activateAutoplayLine = (start?: ThotType, preferredPlayLeafId?: string | null) => {
    const playLeaf = this._resolvePlayLeaf(start, preferredPlayLeafId);
    if (!playLeaf) return;

    this.props.setChainEntry(playLeaf);

    if (this.state.selectedPlayLeafId === playLeaf._id) {
      this._syncUrlParams();
      return;
    }

    this.setState({ selectedPlayLeafId: playLeaf._id }, () => {
      this._syncUrlParams();
    });
  };

  _checkChain = (forceReload = false) => {
    const { thot } = this.state;
    if (!thot) return;

    const { thots } = this.props.chain;
    const current = thots[thot._id];

    if (current && !forceReload) {
      this._applyRouteEntry(thot);
      this.setState({ chainLoaded: true }, () => {
        this._activateAutoplayLine(thot, this.props.route.params?.playLeafId || null);
      });
      return;
    }

    apiLoadThotChain(thot)
      .then((_) => {
        this._applyRouteEntry(thot);
        this.setState({ chainLoaded: true }, () => {
          this._activateAutoplayLine(thot, this.props.route.params?.playLeafId || null);
        });
      })
      .catch((_) => {});
  };

  _newThot = () => {
    const { thot } = this.state;
    if (!thot) return;

    const params: any = { parent: thot._id, addThot: this._addChildThot };
    params.root = thot.root ? (typeof thot.root === "string" ? thot.root : thot.root._id) : thot._id;

    this._video?.pauseAsync();
    this.props.navigation.navigate("NewThot", params);
  };

  _addChildThot = () => {
    this.setState({ chainLoaded: false });
    this.props.setReloadHomeList();
    this._checkChain(true);
  };

  _closeThot = () => {
    this._video?.stopAsync();
    if (this.props.route.params?.backToHome) {
      this.props.navigation.navigate("Home");
      return;
    }
    if (this.props.navigation.canGoBack()) this.props.navigation.goBack();
    else this.props.navigation.navigate("Home");
  };

  _goToThot = (thot: ThotType) => {
    if (!thot) return;
    const preferredPlayLeafId = this.state.selectedPlayLeafId || this.props.route.params?.playLeafId || null;
    const shouldExitReplyMode = !this.state.storyMode && this._getSiblingCount(thot) < 2;
    const keepBackstoryOpen = this.state.headerBackstoryOpen;
    const replyExitLeafId =
      shouldExitReplyMode
        ? (() => {
            const latestLeaf = this._findLatestLeaf(this.props.chain.root || thot);
            const latestLeafLine = latestLeaf ? this._buildLineToRootFromThot(latestLeaf) : [];
            if (latestLeaf && latestLeafLine.includes(thot._id)) return latestLeaf._id;
            return this._findMostVotedPlayLeaf(thot)?._id || null;
          })()
        : null;

    this._video?.stopAsync();

    this.setState(
      {
        thot,
        vote: null,
        videoReadyForDisplay: false,
        isVideoPlaying: true,
        treeCenterId: thot._id,
        previewNodeId: null,
        previewBranchRootId: null,
        headerCollapsedByUser: false,
        headerExpandedByUser: keepBackstoryOpen ? true : false,
        headerBackstoryOpen: keepBackstoryOpen,
        treePanX: 0,
        treePanY: 0,
        blockedNudgeX: 0,
        blockedNudgeY: 0,
        isDraggingTree: false,
        storyMode: shouldExitReplyMode ? true : this.state.storyMode,
        replyLoopStartId: shouldExitReplyMode ? null : this.state.replyLoopStartId,
      },
      () => {
        this._getUserVoteForThot();
        this._applyRouteEntry(thot);
        this._activateAutoplayLine(thot, replyExitLeafId || preferredPlayLeafId);
        if (keepBackstoryOpen) this._scrollHeaderBackstoryToEnd();
      }
    );
  };

  _setVideoRef = (ref) => {
    this._video = ref;
  };

  _onPlaybackStatusUpdate = (thotId: string, playbackStatus) => {
    if (thotId !== this.state.thot?._id) return;

    if (playbackStatus?.isLoaded) {
      const nextPositionMillis = playbackStatus.positionMillis || 0;
      const nextDurationMillis = playbackStatus.durationMillis || 0;
      if (nextPositionMillis !== this.state.videoPositionMillis || nextDurationMillis !== this.state.videoDurationMillis) {
        this.setState({ videoPositionMillis: nextPositionMillis, videoDurationMillis: nextDurationMillis });
      }
    }

    if (!this.state.videoReadyForDisplay && playbackStatus?.isLoaded && (playbackStatus?.isPlaying || (playbackStatus?.positionMillis || 0) > 0)) {
      this.setState({ videoReadyForDisplay: true });
    }

    if (playbackStatus?.isLoaded && playbackStatus?.isPlaying && !this.state.isVideoPlaying) {
      this.setState({ isVideoPlaying: true });
    }

    if (playbackStatus.isLoaded && playbackStatus.didJustFinish && !playbackStatus.isLooping && this.state.autoplay) {
      this._nextVideo();
    }
  };

  _onVideoReadyForDisplay = () => {
    if (!this.state.videoReadyForDisplay) this.setState({ videoReadyForDisplay: true });
  };

  _seekVideoToProgress = async (ratio: number) => {
    const duration = this.state.videoDurationMillis || 0;
    if (!duration) return;

    const clamped = Math.max(0, Math.min(1, ratio));
    try {
      await this._video?.setPositionAsync(Math.round(duration * clamped));
    } catch (_) {}
  };

  _seekVideoFromEvent = (evt: any, barWidth: number) => {
    if (!barWidth) return;
    const x = evt?.nativeEvent?.locationX;
    if (typeof x !== "number") return;
    this._seekVideoToProgress(x / barWidth);
  };

  _getParentId = (item?: ThotType): string | undefined => {
    if (!item || !item.parent) return undefined;
    return typeof item.parent === "string" ? item.parent : item.parent._id;
  };

  _getFirstChild = (item?: ThotType): ThotType | undefined => {
    const { thots } = this.props.chain;
    if (!item?.children?.length || !thots) return undefined;
    return thots[item.children[0]];
  };

  _getSiblingCount = (item?: ThotType): number => {
    const { thots } = this.props.chain;
    const parentId = this._getParentId(item);
    if (!parentId || !thots?.[parentId]?.children) return 0;
    return thots[parentId].children.length || 0;
  };

  _getLeafDescendants = (start?: ThotType): ThotType[] => {
    const { thots } = this.props.chain;
    if (!start || !thots) return [];

    const leaves: ThotType[] = [];
    const visited = new Set<string>();

    const walk = (node?: ThotType) => {
      if (!node || visited.has(node._id)) return;
      visited.add(node._id);

      const children = (node.children || []).map((id) => thots[id]).filter(Boolean);
      if (!children.length) {
        leaves.push(node);
        return;
      }

      children.forEach(walk);
    };

    walk(start);
    return leaves;
  };

  _findLatestLeaf = (start?: ThotType): ThotType | undefined => {
    return this._getLeafDescendants(start).sort((a, b) => {
      const aTime = Date.parse(a.created_at || "") || 0;
      const bTime = Date.parse(b.created_at || "") || 0;
      if (bTime !== aTime) return bTime - aTime;
      return (b.votes || 0) - (a.votes || 0);
    })[0];
  };

  _findMostVotedPlayLeaf = (start?: ThotType): ThotType | undefined => {
    const { thots } = this.props.chain;
    if (!thots || !start) return undefined;

    const visited = new Set<string>();
    let cursor: ThotType | undefined = start;

    while (cursor && !visited.has(cursor._id)) {
      visited.add(cursor._id);
      const children = (cursor.children || [])
        .map((id) => thots[id])
        .filter(Boolean)
        .sort((a, b) => {
          const voteDelta = (b.votes || 0) - (a.votes || 0);
          if (voteDelta !== 0) return voteDelta;
          const aTime = Date.parse(a.created_at || "") || 0;
          const bTime = Date.parse(b.created_at || "") || 0;
          return bTime - aTime;
        });
      const next = children[0];
      if (!next || next._id === cursor._id) return cursor;
      cursor = next;
    }

    return cursor;
  };

  _goSwipeParent = () => {
    const { thot } = this.state;
    if (!thot?.parent) return this._nudgeBlockedDirection(1, 0);
    const { thots } = this.props.chain;
    const parent = typeof thot.parent === "string" ? thots[thot.parent] : thot.parent;
    if (parent) this._goToThot(parent);
    else this._nudgeBlockedDirection(1, 0);
  };

  _goSwipeChild = () => {
    const { thot } = this.state;
    const selectedPathChildByParent = this._buildSelectedPathChildByParent();
    const selectedChildId = thot ? selectedPathChildByParent[thot._id] : undefined;
    const child = selectedChildId ? this.props.chain.thots?.[selectedChildId] : this._getFirstChild(thot || undefined);
    if (child) this._goToThot(child);
    else this._nudgeBlockedDirection(-1, 0);
  };

  _goSibling = (delta: number) => {
    const { thot } = this.state;
    const { thots } = this.props.chain;
    if (!thot || !thots) return;

    const parentId = this._getParentId(thot);
    if (!parentId || !thots[parentId]?.children?.length) return this._nudgeBlockedDirection(0, delta < 0 ? 1 : -1);

    const siblings = thots[parentId].children;
    const idx = siblings.findIndex((id) => id === thot._id);
    if (idx < 0) return this._nudgeBlockedDirection(0, delta < 0 ? 1 : -1);

    const nextIdx = idx + delta;
    if (nextIdx < 0 || nextIdx >= siblings.length) return this._nudgeBlockedDirection(0, delta < 0 ? 1 : -1);

    const sibling = thots[siblings[nextIdx]];
    if (sibling) this._goToThot(sibling);
    else this._nudgeBlockedDirection(0, delta < 0 ? 1 : -1);
  };

  _nudgeBlockedDirection = (xDir: number, yDir: number) => {
    if (this.state.zoomLevel !== 0) return;

    if (this._blockedNudgeTimer) clearTimeout(this._blockedNudgeTimer);

    const distance = 18;
    this.setState({
      blockedNudgeX: xDir * distance,
      blockedNudgeY: yDir * distance,
    });

    this._blockedNudgeTimer = setTimeout(() => {
      this.setState({ blockedNudgeX: 0, blockedNudgeY: 0 });
      this._blockedNudgeTimer = null;
    }, 140);
  };

  _playChainFromCurrentToLeaf = (leaf: ThotType) => {
    const { thot } = this.state;
    if (!leaf || !thot) return;

    const leafLine = this._buildLineToRootFromThot(leaf);
    const startThot = leafLine.includes(thot._id) ? thot : this._findLowestCommonAncestor(thot, leaf) || thot;

    this.props.setChainEntry(leaf);
    this.setState(
      {
        thot: startThot,
        vote: null,
        autoplay: true,
        isVideoPlaying: true,
        storyMode: true,
        selectedPlayLeafId: leaf._id,
        previewNodeId: null,
        previewBranchRootId: null,
        treeCenterId: startThot._id,
        videoReadyForDisplay: false,
        treePanX: 0,
        treePanY: 0,
        blockedNudgeX: 0,
        blockedNudgeY: 0,
        isDraggingTree: false,
      },
      () => {
        this._getUserVoteForThot();
        this._applyRouteEntry(startThot);
        this._syncUrlParams();
      }
    );
  };

  _reloadChainFromRootToLeaf = (leaf: ThotType) => {
    const { root, thots } = this.props.chain;
    if (!leaf || !root || !thots) return;

    const rootThot = thots[root._id] || root;
    this.props.setChainEntry(leaf);

    this.setState(
      {
        thot: rootThot,
        vote: null,
        autoplay: true,
        isVideoPlaying: true,
        storyMode: true,
        selectedPlayLeafId: leaf._id,
        previewNodeId: null,
        previewBranchRootId: null,
        treeCenterId: rootThot._id,
        videoReadyForDisplay: false,
        treePanX: 0,
        treePanY: 0,
        blockedNudgeX: 0,
        blockedNudgeY: 0,
        isDraggingTree: false,
      },
      () => {
        this._getUserVoteForThot();
        this._syncUrlParams();
      }
    );
  };

  _buildSelectedPathChildByParent = (): Record<string, string> => {
    const { thots } = this.props.chain;
    const { selectedPlayLeafId } = this.state;
    const childByParent: Record<string, string> = {};

    if (!thots || !selectedPlayLeafId || !thots[selectedPlayLeafId]) return childByParent;

    let child: ThotType | undefined = thots[selectedPlayLeafId];
    while (child) {
      const parentId = this._getParentId(child);
      if (!parentId) break;

      childByParent[parentId] = child._id;
      child = thots[parentId];
    }

    return childByParent;
  };

  _buildLineToRootFromThot = (start?: ThotType): string[] => {
    const { thots } = this.props.chain;
    const line: string[] = [];

    if (!thots || !start) return line;

    let cursor: ThotType | undefined = start;
    while (cursor) {
      line.push(cursor._id);
      const parentId = this._getParentId(cursor);
      if (!parentId) break;
      cursor = thots[parentId];
    }

    return line.reverse();
  };

  _findLowestCommonAncestor = (a?: ThotType, b?: ThotType): ThotType | undefined => {
    const { thots } = this.props.chain;
    if (!thots || !a || !b) return undefined;

    const aLine = this._buildLineToRootFromThot(a);
    const bLine = this._buildLineToRootFromThot(b);
    const sharedLength = Math.min(aLine.length, bLine.length);
    let lastSharedId: string | undefined;

    for (let i = 0; i < sharedLength; i++) {
      if (aLine[i] !== bLine[i]) break;
      lastSharedId = aLine[i];
    }

    return lastSharedId ? thots[lastSharedId] : undefined;
  };

  _buildCurrentStoryLineToRoot = (): string[] => {
    const { thot, selectedPlayLeafId } = this.state;
    const { thots, entry } = this.props.chain;

    if (selectedPlayLeafId && thots?.[selectedPlayLeafId]) {
      return this._buildLineToRootFromThot(thots[selectedPlayLeafId]);
    }

    return this._buildLineToRootFromThot(thot || entry || undefined);
  };

  _buildUpcomingStoryLine = (): Set<string> => {
    const { thot } = this.state;
    const line = new Set<string>();

    if (!thot) return line;

    let cursor: ThotType | undefined = thot;
    while (cursor && !line.has(cursor._id)) {
      line.add(cursor._id);
      cursor = this._nextStoryThot(cursor);
    }

    return line;
  };

  _nextStoryThot = (thot: ThotType): ThotType | undefined => {
    const { thots, entry } = this.props.chain;
    if (!thots || !entry) return undefined;

    const selectedPathMap = this._buildSelectedPathChildByParent();
    const forcedNextId = selectedPathMap[thot._id];
    if (forcedNextId) return thots[forcedNextId];

    let next = entry;
    while (true) {
      if (!next.parent) {
        next = undefined;
        break;
      }

      const parentId = this._getParentId(next);
      if (!parentId) break;
      if (parentId === thot._id) break;
      next = thots[parentId];
    }

    if (next) return next;
    return this._getFirstChild(thot);
  };

  _nextParallelReply = (thot: ThotType): ThotType | undefined => {
    const { thots } = this.props.chain;
    if (!thots) return undefined;

    const parentId = this._getParentId(thot);
    if (!parentId) return undefined;

    const siblings = thots[parentId]?.children || [];
    const currentIndex = siblings.findIndex((id) => id === thot._id);
    if (currentIndex < 0 || siblings.length < 2) return undefined;

    return thots[siblings[(currentIndex + 1) % siblings.length]];
  };

  _nextVideo = () => {
    const { thot, storyMode, replyLoopStartId } = this.state;
    if (!thot) return;

    const next = storyMode ? this._nextStoryThot(thot) : this._nextParallelReply(thot);
    if (!next) return;

    if (!storyMode && replyLoopStartId && next._id === replyLoopStartId) {
      this.setState({ storyMode: true, replyLoopStartId: null }, () => {
        this._goToThot(next);
      });
      return;
    }

    this._goToThot(next);
  };

  _activateThot = async (item: ThotType) => {
    if (!item) return;

    if (item._id !== this.state.thot?._id) {
      if (this.state.zoomLevel > 0) {
        this.setState(
          {
            treeCenterId: item._id,
            treePanX: 0,
            treePanY: 0,
            blockedNudgeX: 0,
            blockedNudgeY: 0,
            previewNodeId: null,
            previewBranchRootId: null,
            isDraggingTree: false,
          },
          () => this._goToThot(item)
        );
      } else {
        this._goToThot(item);
      }
      return;
    }

    try {
      if (this.state.isVideoPlaying) {
        await this._video?.pauseAsync();
        this.setState({ isVideoPlaying: false });
      } else {
        await this._video?.playAsync();
        this.setState({ isVideoPlaying: true });
      }
    } catch (_) {}
  };

  _onNodePress = (item: ThotType) => {
    if (!item) return;

    if ((this._suppressedNodePressUntil[item._id] || 0) > Date.now()) return;
    this._activateThot(item);
  };

  _onNodeDoubleClick = (item: ThotType) => {
    if (!item || this.state.zoomLevel === 0) return;

    this.setState(
      {
        zoomLevel: 0,
        treeCenterId: item._id,
        treePanX: 0,
        treePanY: 0,
        blockedNudgeX: 0,
        blockedNudgeY: 0,
        previewNodeId: null,
        previewBranchRootId: null,
        isDraggingTree: false,
      },
      () => {
        this._activateThot(item);
      }
    );
  };

  _getTreeLayout = () => {
    const { thot, treeHeight, treeWidth, storyMode } = this.state;
    const { thots, root } = this.props.chain;
    if (!thot || !thots || !root) return null;

    const all = Object.values(thots);
    const childrenByParent: Record<string, ThotType[]> = {};

    all.forEach((item) => {
      const parentId = this._getParentId(item);
      if (!parentId) return;
      if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
      childrenByParent[parentId].push(item);
    });

    const sortChildren = (items: ThotType[] = []) => [...items].sort((a, b) => (b.votes || 0) - (a.votes || 0));

    const coordsById: Record<string, { x: number; y: number }> = {};
    const spanById: Record<string, number> = {};

    const computeSpan = (node?: ThotType): number => {
      if (!node) return 0;
      if (spanById[node._id] !== undefined) return spanById[node._id];
      const children = sortChildren(childrenByParent[node._id] || []);
      if (!children.length) return (spanById[node._id] = 1);
      return (spanById[node._id] = Math.max(1, children.reduce((sum, child) => sum + computeSpan(child), 0)));
    };

    const assign = (node?: ThotType, x = 0, startRow = 0) => {
      if (!node || coordsById[node._id]) return;
      coordsById[node._id] = { x, y: startRow * 10 };

      let childStartRow = startRow;
      sortChildren(childrenByParent[node._id] || []).forEach((child) => {
        assign(child, x + 10, childStartRow);
        childStartRow += computeSpan(child);
      });
    };

    computeSpan(root);
    assign(root, 0, 0);

    const currentStoryLine = this._buildCurrentStoryLineToRoot();
    const lineAnchorY = coordsById[root._id]?.y ?? 0;

    const shiftSubtreeY = (nodeId: string, deltaY: number) => {
      if (!deltaY) return;
      const stack: string[] = [nodeId];
      const visited = new Set<string>();

      while (stack.length) {
        const id = stack.pop();
        if (!id || visited.has(id)) continue;
        visited.add(id);

        const pos = coordsById[id];
        if (pos) coordsById[id] = { ...pos, y: pos.y + deltaY };

        const children = childrenByParent[id] || [];
        children.forEach((child) => stack.push(child._id));
      }
    };

    const getSubtreeBounds = (nodeId: string): { minY: number; maxY: number } | null => {
      const stack: string[] = [nodeId];
      const visited = new Set<string>();
      let minY = Infinity;
      let maxY = -Infinity;

      while (stack.length) {
        const id = stack.pop();
        if (!id || visited.has(id)) continue;
        visited.add(id);

        const pos = coordsById[id];
        if (pos) {
          minY = Math.min(minY, pos.y);
          maxY = Math.max(maxY, pos.y);
        }

        const children = childrenByParent[id] || [];
        children.forEach((child) => stack.push(child._id));
      }

      if (!isFinite(minY) || !isFinite(maxY)) return null;
      return { minY, maxY };
    };

    const packSiblingSubtreesByActualBounds = (parentId: string) => {
      const siblings = sortChildren(childrenByParent[parentId] || []);
      if (!siblings.length) return;

      const laneGap = 10;
      let cursor = Number.NaN;

      siblings.forEach((sibling) => {
        const bounds = getSubtreeBounds(sibling._id);
        if (!bounds) return;

        if (Number.isNaN(cursor)) cursor = bounds.minY;

        const deltaY = cursor - bounds.minY;
        if (deltaY) shiftSubtreeY(sibling._id, deltaY);

        const shiftedBounds = getSubtreeBounds(sibling._id);
        if (!shiftedBounds) return;

        cursor = shiftedBounds.maxY + laneGap;
      });
    };

    const alignAndPackTopDown = () => {
      for (let depth = 1; depth < currentStoryLine.length; depth++) {
        const parentId = currentStoryLine[depth - 1];
        const lineNodeId = currentStoryLine[depth];
        const lineNodeY = coordsById[lineNodeId]?.y;
        if (lineNodeY === undefined) continue;

        const deltaY = lineAnchorY - lineNodeY;
        if (deltaY) {
          const siblings = sortChildren(childrenByParent[parentId] || []);
          siblings.forEach((sibling) => shiftSubtreeY(sibling._id, deltaY));
        }

        packSiblingSubtreesByActualBounds(parentId);
      }
    };

    alignAndPackTopDown();
    for (let depth = currentStoryLine.length - 2; depth >= 0; depth--) {
      const ancestorParentId = currentStoryLine[depth];
      packSiblingSubtreesByActualBounds(ancestorParentId);
    }
    alignAndPackTopDown();

    currentStoryLine.forEach((id) => {
      if (!coordsById[id]) return;
      coordsById[id] = { ...coordsById[id], y: lineAnchorY };
    });

    const centeredNodeId = this.state.treeCenterId && coordsById[this.state.treeCenterId] ? this.state.treeCenterId : thot._id;
    const currentPos = coordsById[centeredNodeId] || { x: 0, y: 0 };
    const baseNodeW = Math.max(120, Math.min(treeWidth * 0.54, 260));
    const nodeW = baseNodeW + (this.state.zoomLevel === 0 ? 100 : 0);
    const imageH =
      treeHeight > 0
        ? Math.round(nodeW * ((treeHeight || 1) / Math.max(treeWidth || 1, 1)))
        : Math.round(nodeW * 1.2);
    const minGap = 50;
    const xScale = (nodeW + minGap) / 10;
    const yScale = (imageH + minGap) / 10;

    const playSize = Math.max(14, Math.round(nodeW * 0.11));
    const leafNodes = all.filter((item) => !(item.children && item.children.length));
    const controlNodes = leafNodes
      .map((leaf) => {
        const leafPos = coordsById[leaf._id];
        if (!leafPos) return null;
        return {
          keyPlay: `play-${leaf._id}`,
          leaf,
          playX: leafPos.x + 10,
          y: leafPos.y,
        };
      })
      .filter(Boolean) as Array<{ keyPlay: string; leaf: ThotType; playX: number; y: number }>;

    return {
      all,
      childrenByParent,
      coordsById,
      currentPos,
      controlNodes,
      imageH,
      nodeW,
      playSize,
      storyMode,
      xScale,
      yScale,
      upcomingStoryLine: this._buildUpcomingStoryLine(),
    };
  };

  _snapTreeToNearestThot = () => {
    const layout = this._getTreeLayout();
    const { thot, treeWidth, treeHeight, treePanX, treePanY, zoomLevel } = this.state;
    if (!layout || !thot || !treeWidth || !treeHeight) return;

    const scale = this._getZoomScale(zoomLevel, layout, thot);
    const currentX = layout.currentPos.x * layout.xScale;
    const currentY = layout.currentPos.y * layout.yScale;
    const viewportFocus = this._getViewportFocusPoint(zoomLevel);
    const tx = viewportFocus.x - (currentX + layout.nodeW / 2) * scale + treePanX;
    const ty = viewportFocus.y - (currentY + layout.imageH / 2) * scale + treePanY;
    const centerX = viewportFocus.x;
    const centerY = viewportFocus.y;

    let closest: ThotType | null = null;
    let closestDistance = Infinity;

    layout.all.forEach((item) => {
      const pos = layout.coordsById[item._id];
      if (!pos) return;

      const nodeCenterX = (pos.x * layout.xScale + layout.nodeW / 2) * scale + tx;
      const nodeCenterY = (pos.y * layout.yScale + layout.imageH / 2) * scale + ty;
      const dx = nodeCenterX - centerX;
      const dy = nodeCenterY - centerY;
      const distance = dx * dx + dy * dy;

      if (distance < closestDistance) {
        closestDistance = distance;
        closest = item;
      }
    });

    this.setState({
      treeCenterId: closest?._id || thot._id,
      previewNodeId: null,
      previewBranchRootId: null,
      treePanX: 0,
      treePanY: 0,
      blockedNudgeX: 0,
      blockedNudgeY: 0,
      isDraggingTree: false,
    });
  };

  _getTouchPoint = (evt) => {
    const touch = evt?.nativeEvent?.touches?.[0] || evt?.nativeEvent?.changedTouches?.[0];
    if (touch) return { x: touch.pageX ?? touch.locationX ?? 0, y: touch.pageY ?? touch.locationY ?? 0 };
    if (typeof evt?.nativeEvent?.pageX === "number" && typeof evt?.nativeEvent?.pageY === "number") {
      return { x: evt.nativeEvent.pageX, y: evt.nativeEvent.pageY };
    }
    return null;
  };

  _setPreviewNode = (previewNodeId: string | null, previewBranchRootId?: string | null) => {
    const nextBranchRootId = previewBranchRootId === undefined ? this.state.previewBranchRootId : previewBranchRootId;
    if (this.state.previewNodeId === previewNodeId && this.state.previewBranchRootId === nextBranchRootId) return;
    this.setState({ previewNodeId, previewBranchRootId: nextBranchRootId });
  };

  _onNodeHoverIn = (nodeId: string) => {
    const branchRootId = this.state.previewBranchRootId || nodeId;
    this._setPreviewNode(nodeId, branchRootId);
  };

  _onNodeHoverOut = (nodeId: string) => {
    if (this.state.isDraggingTree) return;
    if (this.state.previewNodeId === nodeId) this._setPreviewNode(null, null);
  };

  _onNodePressIn = (nodeId: string, evt) => {
    const branchRootId = this.state.previewBranchRootId || nodeId;
    this._setPreviewNode(nodeId, branchRootId);
    const point = this._getTouchPoint(evt);
    if (!point) return;
    this._nodePressMeta[nodeId] = {
      startedAt: Date.now(),
      startX: point.x,
      startY: point.y,
      moved: false,
    };
  };

  _onNodeTouchMove = (nodeId: string, evt) => {
    const meta = this._nodePressMeta[nodeId];
    const point = this._getTouchPoint(evt);
    if (!meta || !point || meta.moved) return;

    const dx = point.x - meta.startX;
    const dy = point.y - meta.startY;
    if (Math.sqrt(dx * dx + dy * dy) > 10) meta.moved = true;
  };

  _onNodePressOut = (nodeId: string) => {
    const meta = this._nodePressMeta[nodeId];
    if (meta) {
      const tooLong = Date.now() - meta.startedAt > 400;
      if (tooLong || meta.moved) this._suppressedNodePressUntil[nodeId] = Date.now() + 250;
      delete this._nodePressMeta[nodeId];
    }

    if (this.state.zoomLevel > 0 || this.state.isDraggingTree) return;
    if (this.state.previewNodeId === nodeId) this._setPreviewNode(null, null);
  };

  _buildPathEdgeKeys = (nodeIds: string[]): Set<string> => {
    const edgeKeys = new Set<string>();
    for (let i = 1; i < nodeIds.length; i++) {
      edgeKeys.add(`${nodeIds[i - 1]}-${nodeIds[i]}`);
    }
    return edgeKeys;
  };

  _buildFutureStoryLine = (): string[] => {
    const { thot } = this.state;
    const line: string[] = [];
    if (!thot) return line;

    let cursor: ThotType | undefined = thot;
    const visited = new Set<string>();
    while (cursor && !visited.has(cursor._id)) {
      line.push(cursor._id);
      visited.add(cursor._id);
      cursor = this._nextStoryThot(cursor);
    }

    return line;
  };

  _buildReplyEdgeKeys = (): Set<string> => {
    const { thot } = this.state;
    const { thots } = this.props.chain;
    const edgeKeys = new Set<string>();
    if (!thot || !thots) return edgeKeys;

    const parentId = this._getParentId(thot);
    if (!parentId) return edgeKeys;

    const siblings = thots[parentId]?.children || [];
    siblings.forEach((childId) => edgeKeys.add(`${parentId}-${childId}`));
    return edgeKeys;
  };

  _buildConnectorSegments = (keyBase: string, x1: number, y1: number, x2: number, y2: number, backgroundColor: string, thickness: number) => {
    const snappedX1 = Math.round(x1);
    const snappedY1 = Math.round(y1);
    const snappedX2 = Math.round(x2);
    const snappedY2 = Math.round(y2);
    const snappedThickness = Math.max(1, Math.round(thickness));
    const segments: Array<{ key: string; left: number; top: number; width: number; height: number; backgroundColor: string }> = [];

    const verticalTop = Math.min(snappedY1, snappedY2);
    const verticalHeight = Math.abs(snappedY2 - snappedY1) + snappedThickness;
    segments.push({
      key: `${keyBase}-v`,
      left: Math.round(snappedX1 - snappedThickness / 2),
      top: Math.round(verticalTop - snappedThickness / 2),
      width: snappedThickness,
      height: verticalHeight,
      backgroundColor,
    });

    const horizontalLeft = Math.min(snappedX1, snappedX2);
    const horizontalWidth = Math.abs(snappedX2 - snappedX1) + snappedThickness;
    segments.push({
      key: `${keyBase}-h`,
      left: Math.round(horizontalLeft - snappedThickness / 2),
      top: Math.round(snappedY2 - snappedThickness / 2),
      width: horizontalWidth,
      height: snappedThickness,
      backgroundColor,
    });

    return segments;
  };

  _buildControlConnectorSegments = (keyBase: string, x1: number, y1: number, x2: number, y2: number, backgroundColor: string, thickness: number) => {
    const snappedX1 = Math.round(x1);
    const snappedY1 = Math.round(y1);
    const snappedX2 = Math.round(x2);
    const snappedY2 = Math.round(y2);
    const snappedThickness = Math.max(1, Math.round(thickness));
    const segments: Array<{ key: string; left: number; top: number; width: number; height: number; backgroundColor: string }> = [];

    if (snappedY1 !== snappedY2) {
      const verticalTop = Math.min(snappedY1, snappedY2);
      segments.push({
        key: `${keyBase}-v`,
        left: Math.round(snappedX1 - snappedThickness / 2),
        top: verticalTop,
        width: snappedThickness,
        height: Math.abs(snappedY2 - snappedY1),
        backgroundColor,
      });
    }

    if (snappedX1 !== snappedX2) {
      const horizontalLeft = Math.min(snappedX1, snappedX2);
      segments.push({
        key: `${keyBase}-h`,
        left: horizontalLeft,
        top: Math.round(snappedY2 - snappedThickness / 2),
        width: Math.abs(snappedX2 - snappedX1),
        height: snappedThickness,
        backgroundColor,
      });
    }

    return segments;
  };

  _getLineColorPriority = (backgroundColor: string) => {
    if (backgroundColor === "#fff") return 3;
    if (backgroundColor === "#28a745") return 2;
    return 1;
  };

  _dedupeConnectorSegments = (segments: Array<{ key: string; left: number; top: number; width: number; height: number; backgroundColor: string }>) => {
    const deduped = new Map<string, { key: string; left: number; top: number; width: number; height: number; backgroundColor: string }>();

    segments.forEach((segment) => {
      const geometryKey = [
        Math.round(segment.left * 100) / 100,
        Math.round(segment.top * 100) / 100,
        Math.round(segment.width * 100) / 100,
        Math.round(segment.height * 100) / 100,
      ].join(":");
      const existing = deduped.get(geometryKey);

      if (!existing || this._getLineColorPriority(segment.backgroundColor) >= this._getLineColorPriority(existing.backgroundColor)) {
        deduped.set(geometryKey, segment);
      }
    });

    return [...deduped.values()];
  };

  _getFullscreenFocusCenter = (layout, thot: ThotType, nodeHeight: number) => {
    const currentPos = layout.coordsById[thot._id];
    if (!currentPos) return { x: 0, y: 0 };

    return {
      x: currentPos.x * layout.xScale + layout.nodeW / 2,
      y: currentPos.y * layout.yScale + nodeHeight / 2,
    };
  };

  _getViewportFocusPoint = (zoomLevel: 0 | 1) => {
    if (zoomLevel === 0) {
      const leftPadding = 40;
      const rightPadding = 40;
      return {
        x: leftPadding + (this.state.treeWidth - leftPadding - rightPadding) / 2,
        y: this.state.treeHeight / 2,
      };
    }

    return {
      x: this.state.treeWidth / 2,
      y: this.state.treeHeight / 2,
    };
  };

  _isAncestorOrSelf = (ancestorId: string | null | undefined, nodeId: string): boolean => {
    const { thots } = this.props.chain;
    if (!ancestorId || !nodeId || !thots?.[nodeId]) return false;

    let cursor: ThotType | undefined = thots[nodeId];
    const visited = new Set<string>();

    while (cursor && !visited.has(cursor._id)) {
      if (cursor._id === ancestorId) return true;
      visited.add(cursor._id);
      const parentId = this._getParentId(cursor);
      if (!parentId) break;
      cursor = thots[parentId];
    }

    return false;
  };

  _setVote = (value: number) => {
    const { vote, thot } = this.state;
    if (!thot) return;

    if (vote) {
      if (vote.value == value) {
        apiDeleteUserVote(vote._id)
          .then((_) => {
            thot.votes -= value;
            this.setState({ vote: null, thot });
          })
          .catch((_) => {});
      } else {
        apiUpdateUserVote(vote._id, value)
          .then((res: Vote) => {
            thot.votes += value * 2;
            this.setState({ vote: res, thot });
          })
          .catch((_) => {});
      }
    } else {
      apiAddUserVote(thot._id, value)
        .then((res: Vote) => {
          thot.votes = (thot.votes || 0) + value;
          this.setState({ vote: res, thot });
        })
        .catch((_) => {});
    }
  };

  _renderTree = () => {
    const { thot, previewNodeId, previewBranchRootId, zoomLevel, treeHeight, treeWidth, treePanX, treePanY, blockedNudgeX, blockedNudgeY, isDraggingTree, videoReadyForDisplay } = this.state;
    const layout = this._getTreeLayout();
    if (!thot || !layout) return null;

    const scale = this._getZoomScale(zoomLevel, layout, thot);
    const renderedImageH = Math.max(layout.imageH - (zoomLevel === 0 ? 65 / scale : 0), 80);
    const fullscreenFocusCenter = zoomLevel === 0 ? this._getFullscreenFocusCenter(layout, thot, renderedImageH) : null;
    const focusCenterX = fullscreenFocusCenter ? fullscreenFocusCenter.x : layout.currentPos.x * layout.xScale + layout.nodeW / 2;
    const focusCenterY = fullscreenFocusCenter ? fullscreenFocusCenter.y : layout.currentPos.y * layout.yScale + renderedImageH / 2;
    const viewportFocus = this._getViewportFocusPoint(zoomLevel);
    const tx = viewportFocus.x - focusCenterX * scale + treePanX + blockedNudgeX;
    const ty = viewportFocus.y - focusCenterY * scale + treePanY + blockedNudgeY;
    const targetAvatarScreenSize = zoomLevel === 0 ? 94 : 29.4;
    const avatarSize = targetAvatarScreenSize / scale;
    const targetAvatarInset = zoomLevel === 0 ? 15 : 5;
    const avatarInset = targetAvatarInset / scale;
    const avatarCenterX = avatarInset + avatarSize / 2;
    const avatarCenterY = renderedImageH - avatarInset - avatarSize / 2;
    const voteBadgeScale = zoomLevel > 0 ? 0.8 : 1;
    const voteMinWidth = Math.max(((targetAvatarScreenSize * 0.2) * voteBadgeScale) / scale, 18 / scale);
    const voteFontSize = (((scale >= 1 ? 11 : scale >= 0.4 ? 12 : 13) * voteBadgeScale)) / scale;
    const voteLeft = layout.nodeW / 2 - voteMinWidth / 2;
    const voteTop = renderedImageH - avatarInset - voteMinWidth;
    const progressGap = 8 / scale;
    const progressRightInset = (zoomLevel === 0 ? 10 : 5) / scale;
    const progressLeft = avatarCenterX + avatarSize / 2 + progressGap;
    const progressTop = avatarCenterY - 4 / scale;
    const progressWidth = Math.max(layout.nodeW - progressLeft - progressRightInset, 0);
    const progressRatio = this.state.videoDurationMillis > 0 ? Math.max(0, Math.min(1, this.state.videoPositionMillis / this.state.videoDurationMillis)) : 0;
    const currentPathEdgeKeys = this._buildPathEdgeKeys(this._buildLineToRootFromThot(thot));
    const futureEdgeKeys = layout.storyMode ? this._buildPathEdgeKeys(this._buildFutureStoryLine()) : this._buildReplyEdgeKeys();
    const controlMarginRight = 20;
    const lineThickness = 1;
    const activeChainThickness = zoomLevel === 0 ? 5 : 10;
    const branchHoverId = previewBranchRootId || previewNodeId || thot._id;
    const rootNode = this.props.chain.root;
    const rootPos = rootNode ? layout.coordsById[rootNode._id] : null;
    const activePos = layout.coordsById[thot._id] || layout.currentPos;
    const activeParentId = this._getParentId(thot);
    const activeSiblingCount = activeParentId ? (this.props.chain.thots?.[activeParentId]?.children?.length || 0) : 0;

    const controlVisuals = layout.controlNodes
      .map((ctrl) => {
        const leafPos = layout.coordsById[ctrl.leaf._id];
        if (!leafPos) return null;

        const isSelected = this.state.selectedPlayLeafId === ctrl.leaf._id;
        const showControls = isSelected || this._isAncestorOrSelf(branchHoverId, ctrl.leaf._id);
        const screenTop = (ctrl.y * layout.yScale + renderedImageH / 2) * scale + ty - layout.playSize / 2;
        const clampedTop = Math.max(10, Math.min(treeHeight - layout.playSize - 10, screenTop));
        const leafRight = (leafPos.x * layout.xScale + layout.nodeW) * scale + tx;
        const preferredPlayLeft = leafRight + 10;
        const maxPlayLeft = Math.max(treeWidth - controlMarginRight - layout.playSize, 0);
        const playLeft = Math.min(preferredPlayLeft, maxPlayLeft);
        const xLeaf = leafPos.x * layout.xScale + layout.nodeW / 2;
        const yLeaf = leafPos.y * layout.yScale + renderedImageH / 2;
        const xPlay = (playLeft + layout.playSize / 2 - tx) / scale;
        const yPlay = (clampedTop + layout.playSize / 2 - ty) / scale;

        return {
          ...ctrl,
          isSelected,
          showControls,
          clampedTop,
          playLeft,
          xLeaf,
          yLeaf,
          xPlay,
          yPlay,
        };
      })
      .filter(Boolean);

    const rootRestartVisual =
      rootPos && rootNode
        ? (() => {
            const screenTop = (rootPos.y * layout.yScale + renderedImageH / 2) * scale + ty - layout.playSize / 2;
            const clampedTop = Math.max(10, Math.min(treeHeight - layout.playSize - 10, screenTop));
            const rootLeft = (rootPos.x * layout.xScale) * scale + tx;
            const preferredRestartLeft = rootLeft - 10 - layout.playSize;
            const restartLeft = Math.max(controlMarginRight, preferredRestartLeft);
            const xRoot = (rootLeft - tx) / scale;
            const yRoot = (clampedTop + layout.playSize / 2 - ty) / scale;
            const xRestart = (restartLeft + layout.playSize / 2 - tx) / scale;

            return {
              top: clampedTop,
              left: restartLeft,
              line: this._buildControlConnectorSegments("root-restart-edge", xRoot, yRoot, xRestart, yRoot, "#fff", activeChainThickness),
            };
          })()
        : null;

    const replyButtonVisual =
      activePos && activeSiblingCount > 1
        ? (() => {
            const thotLeftX = (activePos.x * layout.xScale) * scale + tx;
            const thotCenterY = (activePos.y * layout.yScale + renderedImageH / 2) * scale + ty;

            return {
              left: zoomLevel === 0 ? 20 : thotLeftX - layout.playSize / 2,
              top: thotCenterY - layout.playSize / 2 + (zoomLevel === 0 ? 50 : 0),
            };
          })()
        : null;

    const treeEdges = this._dedupeConnectorSegments(layout.all
      .map((item) => {
        const parentId = this._getParentId(item);
        if (!parentId || !layout.coordsById[parentId] || !layout.coordsById[item._id]) return null;
        const p = layout.coordsById[parentId];
        const c = layout.coordsById[item._id];
        const x1 = p.x * layout.xScale + layout.nodeW / 2;
        const y1 = p.y * layout.yScale + renderedImageH / 2;
        const x2 = c.x * layout.xScale + layout.nodeW / 2;
        const y2 = c.y * layout.yScale + renderedImageH / 2;
        const key = `${parentId}-${item._id}`;
        const isActiveChain = currentPathEdgeKeys.has(key) || futureEdgeKeys.has(key);
        const backgroundColor = "#fff";
        return this._buildConnectorSegments(key, x1, y1, x2, y2, backgroundColor, isActiveChain ? activeChainThickness : lineThickness);
      })
      .flat()
      .concat(zoomLevel > 0 ? rootRestartVisual?.line || [] : [])
      .filter(Boolean));

    const playButtonEdges =
      zoomLevel > 0
        ? controlVisuals.flatMap((ctrl: any) =>
            this._buildControlConnectorSegments(
              `${ctrl.leaf._id}-play-edge`,
              ctrl.xLeaf,
              ctrl.yLeaf,
              ctrl.xPlay,
              ctrl.yPlay,
              "#fff",
              ctrl.isSelected ? activeChainThickness : lineThickness
            )
          )
        : [];

    return (
      <View style={styles.treeViewport} {...this._panResponder.panHandlers} onLayout={(e) => this.setState({ treeWidth: e.nativeEvent.layout.width, treeHeight: e.nativeEvent.layout.height })}>
        <View
          style={[
            styles.treeWorld,
            isDraggingTree && styles.treeWorldDragging,
            { transform: [{ translateX: tx }, { translateY: ty }, { scale }] },
          ]}
        >
          {[...treeEdges, ...playButtonEdges].map((edge: any) => (
            <View
              key={edge.key}
              style={[
                styles.mapLine,
                { left: edge.left, top: edge.top, width: edge.width, height: edge.height, backgroundColor: edge.backgroundColor },
                Platform.OS === "web"
                  ? (edge.key.includes("play-edge")
                      ? (isDraggingTree
                          ? ({ transitionProperty: "none", transitionDuration: "0ms", transitionTimingFunction: "linear" } as any)
                          : ({ transitionProperty: "left, top, width, height", transitionDuration: "260ms", transitionTimingFunction: "ease-in-out" } as any))
                      : edge.key.includes("root-restart-edge")
                      ? ({ transitionProperty: "top", transitionDuration: "260ms", transitionTimingFunction: "ease-in-out" } as any)
                      : ({ transitionProperty: "left, top, width, height", transitionDuration: "260ms", transitionTimingFunction: "ease-in-out" } as any))
                  : null,
              ]}
            />
          ))}

          {layout.all.map((item) => {
            const pos = layout.coordsById[item._id] || { x: 0, y: 0 };
            const isCurrent = item._id === thot._id;
            const userId = typeof item.user === "string" ? item.user : item.user?._id;
            const profileUri = userId ? apiGetProfileUri(userId) : undefined;
            const thumbUri = apiGetThotThumbnailUri(item._id);
            const isPreviewed = previewNodeId === item._id;
            const showAvatar = isCurrent || isPreviewed;
            const showVoteBadge = zoomLevel > 0 && !!previewNodeId && isPreviewed && !isCurrent;
            const mediaKind = item.media_kind || "video";

            return (
              <Pressable
                key={item._id}
                onPress={() => this._onNodePress(item)}
                onPressIn={(evt) => this._onNodePressIn(item._id, evt)}
                onPressOut={() => this._onNodePressOut(item._id)}
                onHoverIn={() => this._onNodeHoverIn(item._id)}
                onHoverOut={() => this._onNodeHoverOut(item._id)}
                onTouchMove={(evt) => this._onNodeTouchMove(item._id, evt)}
                onLongPress={() => {}}
                focusable={false}
                {...(Platform.OS === "web" ? ({ onContextMenu: (evt) => evt.preventDefault(), onDoubleClick: () => this._onNodeDoubleClick(item) } as any) : null)}
                style={[
                  styles.treeNode,
                  { left: pos.x * layout.xScale, top: pos.y * layout.yScale, width: layout.nodeW, height: renderedImageH },
                  isCurrent && styles.treeNodeCurrent,
                  Platform.OS === "web"
                    ? ({
                        transitionProperty: "left, top, opacity, transform",
                        transitionDuration: "280ms",
                        transitionTimingFunction: "ease-in-out",
                        outlineStyle: "none",
                        WebkitTapHighlightColor: "transparent",
                        WebkitTouchCallout: "none",
                      } as any)
                    : null,
                ]}
              >
                <View style={styles.nodeMediaWrap}>
                  {isCurrent ? (
                    <>
                      {mediaKind === "image" ? (
                        <Image source={{ uri: apiGetThotUri(item._id) }} style={styles.nodeMediaImage} resizeMode="cover" />
                      ) : mediaKind === "url" ? (
                        <Image source={{ uri: thumbUri }} style={styles.nodeMediaImage} resizeMode="cover" />
                      ) : (
                        <>
                          {mediaKind === "audio" ? <Image source={{ uri: thumbUri }} style={styles.nodeMediaPosterOverlay} resizeMode="cover" /> : null}
                          <Video
                            key={item._id}
                            ref={this._setVideoRef}
                            source={{ uri: apiGetThotUri(item._id) }}
                            rate={1.0}
                            volume={1.0}
                            isMuted={false}
                            onReadyForDisplay={this._onVideoReadyForDisplay}
                            onPlaybackStatusUpdate={(status) => this._onPlaybackStatusUpdate(item._id, status)}
                            resizeMode={ResizeMode.COVER}
                            shouldPlay={this.state.isVideoPlaying}
                            isLooping={!this.state.autoplay}
                            style={mediaKind === "audio" ? styles.nodeAudioMedia : styles.nodeMedia}
                            videoStyle={styles.video}
                          />
                          {mediaKind !== "audio" && !videoReadyForDisplay ? <Image source={{ uri: thumbUri }} style={styles.nodeMediaPosterOverlay} resizeMode="cover" /> : null}
                        </>
                      )}
                      {(mediaKind === "video" || mediaKind === "audio") && progressWidth > 0 ? (
                        <View
                          onStartShouldSetResponder={() => true}
                          onMoveShouldSetResponder={() => true}
                          onResponderGrant={(evt) => this._seekVideoFromEvent(evt, progressWidth)}
                          onResponderMove={(evt) => this._seekVideoFromEvent(evt, progressWidth)}
                          style={[
                            styles.nodeProgressTrack,
                            {
                              left: progressLeft,
                              top: progressTop,
                              width: progressWidth,
                            },
                          ]}
                        >
                          <View style={[styles.nodeProgressFill, { width: progressWidth * progressRatio }]} />
                        </View>
                      ) : null}
                    </>
                  ) : (
                    <Image source={{ uri: thumbUri }} style={styles.nodeMediaImage} resizeMode="cover" />
                  )}
                </View>
                <View pointerEvents="box-none" style={styles.nodeOverlay}>
                  <View style={[styles.nodeAvatarDock, { left: avatarCenterX, top: avatarCenterY }]}>
                    <View
                      style={[
                        styles.nodeAvatarWrap,
                        {
                          width: avatarSize,
                          height: avatarSize,
                          borderRadius: avatarSize / 2,
                          opacity: showAvatar ? 1 : 0,
                          marginLeft: -avatarSize / 2,
                          marginTop: -avatarSize / 2,
                          transform: [{ scale: showAvatar ? 1 : 0.25 }],
                        },
                      ]}
                    >
                      <Image source={profileUri ? { uri: profileUri } : NO_PROFILE} defaultSource={NO_PROFILE} style={[styles.nodeAvatar, { borderRadius: avatarSize / 2 }]} />
                    </View>
                  </View>
                  <View
                    style={[
                      styles.nodeVoteBadge,
                      {
                        minWidth: voteMinWidth,
                        height: voteMinWidth,
                        borderRadius: voteMinWidth / 2,
                        paddingHorizontal: voteMinWidth * 0.28,
                        left: voteLeft,
                        top: voteTop,
                        opacity: showVoteBadge ? 1 : 0,
                        transform: [{ scale: showVoteBadge ? 1 : 0.2 }],
                      },
                    ]}
                  >
                    <Text selectable={false} style={[styles.nodeVoteBadgeText, { fontSize: voteFontSize }, Platform.OS === "web" ? ({ userSelect: "none" } as any) : null]}>
                      {item.votes || 0}
                    </Text>
                  </View>

                </View>
              </Pressable>
            );
          })}
        </View>

        {controlVisuals.map((ctrl: any) => {
          const { isSelected, showControls, clampedTop, playLeft } = ctrl;
          return (
            <React.Fragment key={`controls-${ctrl.leaf._id}`}>
              <Pressable
                key={ctrl.keyPlay}
                onPress={() => this._playChainFromCurrentToLeaf(ctrl.leaf)}
                pointerEvents={this.state.headerBackstoryOpen ? "none" : "auto"}
                style={[
                  styles.mapPlayNode,
                  showControls ? styles.mapPlayNodeVisible : styles.mapPlayNodeHidden,
                  this.state.headerBackstoryOpen && styles.backstoryButtonsFaded,
                  isDraggingTree && styles.mapPlayNodeDragging,
                  {
                    width: layout.playSize,
                    height: layout.playSize,
                    borderRadius: layout.playSize / 2,
                    left: playLeft,
                    top: clampedTop,
                  },
                  this.state.storyMode ? styles.mapPlayNodeActive : styles.mapPlayNodeInactive,
                  isSelected && styles.mapPlayNodeSelected,
                ]}
              >
                <Text style={styles.mapPlayNodeIcon}>▶</Text>
              </Pressable>
            </React.Fragment>
          );
        })}

        {rootRestartVisual && this.state.selectedPlayLeafId && this.props.chain.thots?.[this.state.selectedPlayLeafId] ? (
          <Pressable
            key="root-restart-button"
            onPress={() => this._reloadChainFromRootToLeaf(this.props.chain.thots![this.state.selectedPlayLeafId!])}
            pointerEvents={this.state.headerBackstoryOpen ? "none" : "auto"}
            style={[
              styles.mapPlayNode,
              styles.mapPlayNodeActive,
              styles.mapPlayNodeVisible,
              styles.mapRestartNodeTop,
              this.state.headerBackstoryOpen && styles.backstoryButtonsFaded,
              isDraggingTree && styles.mapPlayNodeDragging,
              {
                width: layout.playSize,
                height: layout.playSize,
                borderRadius: layout.playSize / 2,
                left: rootRestartVisual.left,
                top: rootRestartVisual.top,
              },
            ]}
          >
            <Text style={styles.mapReloadNodeIcon}>⏮</Text>
          </Pressable>
        ) : null}

        {replyButtonVisual ? (
          <Pressable
            key="current-reply-button"
            onPress={() =>
              this.setState((prev) => ({
                storyMode: !prev.storyMode,
                replyLoopStartId: prev.storyMode ? thot._id : null,
              }))
            }
            pointerEvents={this.state.headerBackstoryOpen ? "none" : "auto"}
            style={[
              styles.mapPlayNode,
              styles.mapPlayNodeVisible,
              this.state.headerBackstoryOpen && styles.backstoryButtonsFaded,
              isDraggingTree && styles.mapPlayNodeDragging,
              {
                width: layout.playSize,
                height: layout.playSize,
                borderRadius: layout.playSize / 2,
                left: replyButtonVisual.left,
                top: replyButtonVisual.top,
              },
              this.state.storyMode ? styles.mapPlayNodeInactive : styles.mapPlayNodeActive,
            ]}
          >
            <Text style={styles.replyNodeIcon}>⎇</Text>
          </Pressable>
        ) : null}
      </View>
    );
  };

  render() {
    const { thot, vote, chainLoaded, zoomLevel } = this.state;
    if (!thot || !thot._id) return <View />;

    const voted = (vote && vote.value) || 0;
    const replyButtonTitle = "[◉°]｡⊹˚₊";

    return (
      <View style={{ flex: 1, backgroundColor: colorStyle.appBg }}>
        {chainLoaded && this._renderTree()}

        <View pointerEvents="box-none" style={styles.overlay}>
          <View pointerEvents="none" style={styles.bottomGradient} />
          <View pointerEvents={this.state.headerBackstoryOpen ? "none" : "auto"} style={[styles.overlayBottom, this.state.headerBackstoryOpen && styles.backstoryButtonsFaded]}>
            <View style={styles.bottomActionRow}>
              <View style={styles.bottomLeftControls}>
                <TouchableOpacity style={styles.bottomCircleButton} onPress={this._closeThot}>
                  <Text style={styles.bottomCircleButtonIcon}>←</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.bottomCircleButton} onPress={() => this._setZoomLevel(zoomLevel === 0 ? 1 : 0)}>
                  <Text style={styles.bottomCircleButtonIcon}>{zoomLevel === 0 ? "⌕-" : "↙↗"}</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.bottomCenter}>
                <View style={styles.votesBottom}>
                <TouchableOpacity style={[styles.round, styles.bottomVote, styles.bottomVoteMinus, voted !== -1 && styles.voteInactive, voted == -1 && styles.voteActive]} onPress={() => this._setVote(-1)}>
                  <Text>(·•᷄‎ࡇ•᷅ )</Text>
                </TouchableOpacity>
                <View style={[styles.round, styles.bottomVote, styles.bottomVoteCount, styles.voteActive]}>
                  <Text selectable={false} style={Platform.OS === "web" ? ({ userSelect: "none" } as any) : null}>
                    {thot.votes || 0}
                  </Text>
                </View>
                <TouchableOpacity style={[styles.round, styles.bottomVote, styles.bottomVotePlus, voted !== 1 && styles.voteInactive, voted == 1 && styles.voteActive]} onPress={() => this._setVote(1)}>
                  <Text style={styles.bottomVotePlusText}>(˶ᵔᵕᵔ˶)</Text>
                </TouchableOpacity>
                </View>
              </View>
              <TouchableOpacity style={[styles.btn, styles.bottomReplyButton]} onPress={this._newThot}>
                <Text>{replyButtonTitle}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  }
}

const mapStateToProps = ({ chain }) => ({ chain });

export default connect(mapStateToProps, {
  addThotToChain,
  setChainEntry,
  setReloadHomeList,
})(ThotScreen);

const styles = StyleSheet.create({
  header: {
    height: 56,
    backgroundColor: "transparent",
    borderBottomWidth: 0,
    elevation: 0,
    shadowOpacity: 0,
  },
  headerNavItem: {
    zIndex: 1,
  },
  headerGradientBackground: {
    ...StyleSheet.absoluteFillObject,
    overflow: "hidden",
  },
  headerGradientLayer: {
    ...StyleSheet.absoluteFillObject,
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "opacity",
          transitionDuration: "280ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  headerGradientLayerDefault: {
    ...(Platform.OS === "web"
      ? ({
          backgroundImage: `linear-gradient(to bottom, ${colorStyle.appBg} 0px, ${colorStyle.appBg} 39px, rgba(237,235,234,0) 100%)`,
        } as any)
      : {
          backgroundColor: "transparent",
        }),
  },
  headerGradientLayerBackstory: {
    ...(Platform.OS === "web"
      ? ({
          backgroundImage: `linear-gradient(to bottom, rgba(237,235,234,1) 0%, rgba(237,235,234,0.5) 100%)`,
        } as any)
      : {
          backgroundColor: "rgba(237,235,234,0.5)",
        }),
  },
  headerGradientLayerVisible: {
    opacity: 1,
  },
  headerGradientLayerHidden: {
    opacity: 0,
  },
  headerTitleContainer: {
    left: 0,
    right: 0,
    marginLeft: 0,
    marginRight: 0,
    width: "100%",
    height: 56,
    flex: 1,
    minWidth: 0,
    overflow: "visible",
    position: "relative",
    backgroundColor: "transparent",
  },
  headerTitleWrap: {
    position: "relative",
    width: "100%",
    overflow: "visible",
    backgroundColor: "transparent",
  },
  headerTitleBubble: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 17,
    paddingLeft: 54,
    paddingRight: 54,
    paddingTop: 0,
    paddingBottom: 0,
    overflow: "hidden",
    zIndex: 0,
    justifyContent: "flex-start",
    alignItems: "center",
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "height",
          transitionDuration: "280ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {
          backgroundColor: "transparent",
        }),
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
    flexShrink: 1,
    lineHeight: 22,
    textAlign: "center",
    width: "100%",
  },
  headerTitlePress: {
    width: "100%",
  },
  headerBackstoryScroll: {
    width: "100%",
    flex: 1,
  },
  headerBackstoryScrollContent: {
    width: "100%",
    paddingTop: 17,
    paddingBottom: 40,
  },
  headerTagRow: {
    width: "100%",
    marginTop: 8,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: 8,
    rowGap: 6,
  },
  headerTag: {
    minHeight: 18,
    paddingHorizontal: 9,
    borderRadius: 999,
    justifyContent: "center",
    alignItems: "center",
  },
  headerMetaTag: {
    backgroundColor: "rgba(255,255,255,0.5)",
  },
  headerActionTag: {
    backgroundColor: "#fff",
  },
  headerActionTagDisabled: {
    opacity: 0.55,
  },
  headerTagText: {
    fontWeight: "500",
    fontSize: 8.4,
    lineHeight: 18,
    textAlign: "center",
  },
  headerBackstoryList: {
    width: "100%",
    alignItems: "center",
  },
  headerBackstoryItem: {
    width: "100%",
    alignItems: "center",
    marginBottom: 10,
  },
  headerBackstoryMetaTag: {
    marginTop: 6,
  },
  video: {
    height: "100%",
    width: "100%",
    ...(Platform.OS === "web" ? ({ objectFit: "cover" } as any) : {}),
  },
  overlay: { ...StyleSheet.absoluteFillObject },
  bottomGradient: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 140,
    ...(Platform.OS === "web"
      ? ({
          backgroundImage: `linear-gradient(to bottom, rgba(237,235,234,0) 0%, ${colorStyle.appBg} 100%)`,
        } as any)
      : {
          backgroundColor: "rgba(237,235,234,0.85)",
          opacity: 0.9,
        }),
  },
  overlayBottom: {
    position: "absolute",
    left: screenStyle.container.padding,
    right: screenStyle.container.padding,
    bottom: 20,
    zIndex: 10,
  },
  backstoryButtonsFaded:
    Platform.OS === "web"
      ? ({
          opacity: 0,
          transitionProperty: "opacity",
          transitionDuration: "220ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {
          opacity: 0,
        },
  treeViewport: { flex: 1, overflow: "hidden" },
  treeWorld: {
    position: "absolute",
    left: 0,
    top: 0,
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "transform",
          transitionDuration: "320ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  treeWorldDragging:
    Platform.OS === "web"
      ? ({
          transitionDuration: "0ms",
          transitionProperty: "none",
        } as any)
      : {},
  mapLine: {
    position: "absolute",
  },
  treeNode: {
    position: "absolute",
    backgroundColor: "rgba(255,255,255,0.92)",
    overflow: "visible",
    borderRadius: 10,
  },
  treeNodeCurrent: {
    zIndex: 5,
    elevation: 5,
  },
  mapPlayNode: {
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.95)",
    opacity: 0,
    transform: [{ scale: 0.2 }],
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "left, top, width, height, transform, opacity, background-color, border-color",
          transitionDuration: "280ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  mapPlayNodeVisible: {
    opacity: 1,
    transform: [{ scale: 1 }],
  },
  mapPlayNodeHidden: {
    opacity: 0,
    transform: [{ scale: 0.2 }],
    pointerEvents: "none",
  },
  mapPlayNodeDragging:
    Platform.OS === "web"
      ? ({
          transitionProperty: "none",
          transitionDuration: "0ms",
        } as any)
      : {},
  mapPlayNodeActive: {
    backgroundColor: "#fff",
    opacity: 1,
  },
  mapPlayNodeInactive: {
    backgroundColor: colorStyle.lightGray,
    opacity: 0.55,
  },
  mapPlayNodeSelected: {
    borderWidth: 0,
  },
  mapReloadNode: {
    backgroundColor: "rgba(245,245,245,0.98)",
  },
  mapPlayNodeIcon: {
    fontSize: 10,
    color: "#111",
    marginLeft: 0.5,
  },
  replyNodeIcon: {
    fontSize: 10,
    color: "#111",
    marginLeft: 0.5,
    fontWeight: "800",
    transform: [{ scaleY: -1 }],
  },
  mapReloadNodeIcon: {
    fontSize: 11,
    color: "#111",
    marginLeft: 0.5,
    ...(Platform.OS === "web" ? ({ fontVariantEmoji: "text" } as any) : null),
  },
  mapRestartNodeTop: {
    zIndex: 3,
    elevation: 3,
  },
  nodeMediaWrap: {
    width: "100%",
    height: "100%",
    backgroundColor: "#fff",
    overflow: "hidden",
    borderRadius: 10,
  },
  nodeOverlay: {
    ...StyleSheet.absoluteFillObject,
  },
  nodeAvatarDock: {
    position: "absolute",
    alignItems: "center",
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "left, top",
          transitionDuration: "280ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  nodeAvatarWrap: {
    position: "relative",
    overflow: "visible",
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "transform, opacity, width, height",
          transitionDuration: "280ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  nodeAvatar: {
    width: "100%",
    height: "100%",
  },
  nodeVoteBadge: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.96)",
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "transform, opacity, min-width, height",
          transitionDuration: "280ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  nodeVoteBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#111",
    includeFontPadding: false,
    textAlignVertical: "center",
    textAlign: "right",
  },
  nodeProgressTrack: {
    position: "absolute",
    height: 8,
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.5)",
    justifyContent: "center",
    overflow: "hidden",
    zIndex: 3,
    elevation: 6,
    pointerEvents: "auto",
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "left, top, width",
          transitionDuration: "280ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  nodeProgressFill: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 999,
    backgroundColor: "#fff",
  },
  nodeMedia: {
    width: "100%",
    height: "100%",
    backgroundColor: "#fff",
  },
  nodeAudioMedia: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0.01,
  },
  nodeMediaImage: {
    width: "100%",
    height: "100%",
    backgroundColor: "#fff",
    ...(Platform.OS === "web" ? ({ objectFit: "cover" } as any) : {}),
  },
  nodeMediaPosterOverlay: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#fff",
    ...(Platform.OS === "web" ? ({ objectFit: "cover" } as any) : {}),
  },
  votesBottom: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 0,
    marginRight: 0,
  },
  bottomActionRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  bottomLeftControls: {
    flexDirection: "row",
    alignItems: "center",
    zIndex: 3,
    elevation: 3,
  },
  bottomCenter: {
    position: "absolute",
    left: 0,
    right: 0,
    alignItems: "center",
    zIndex: 1,
  },
  bottomCircleButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 5,
    zIndex: 3,
  },
  bottomCircleButtonIcon: {
    fontSize: 22,
    color: "#111",
    lineHeight: 22,
    textAlign: "center",
  },
  voteActive: { backgroundColor: "#fff" },
  voteInactive: { backgroundColor: colorStyle.lightGray, opacity: 0.55 },
  bottomVote: {
    width: 48,
    minWidth: 48,
    height: 24,
    paddingHorizontal: 0,
    margin: 0,
    marginRight: 4,
  },
  bottomVoteMinus: {
    marginRight: -4,
    zIndex: 1,
  },
  bottomVoteCount: {
    width: 32,
    minWidth: 32,
    height: 32,
    margin: 0,
    marginRight: 0,
    zIndex: 3,
    elevation: 3,
  },
  bottomVotePlus: {
    marginLeft: -4,
    marginRight: 0,
    zIndex: 1,
  },
  bottomVotePlusText: {
    position: "relative",
    top: -2,
  },
  round: {
    justifyContent: "center",
    alignItems: "center",
    margin: 2,
    paddingHorizontal: 16,
    minWidth: 50,
    height: 50,
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.92)",
  },
  btn: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 999,
  },
  bottomReplyButton: {
    width: "auto",
    minHeight: 48,
    paddingHorizontal: 20,
    marginLeft: "auto",
  },
});
